import { Component, OnChanges, OnInit, Input, Output, EventEmitter, SimpleChanges, ViewChildren, QueryList } from '@angular/core';
import { Router } from '@angular/router';
import { Table } from 'primeng/table';
import { guid } from '@fullcalendar/core/internal';
import { AlertController, ModalController } from '@ionic/angular';
import * as dayjs from 'dayjs';
import { FormatAddress, GoogleMapsUrl } from '../../../services/address.util';
import { QuickCommentComponent } from '../../comments/quick-add/quick-add.component';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { SearchService } from 'src/app/api/search.service';
import { CreatePage } from 'src/app/clients/create/create.page';
import { DetailsPage } from 'src/app/clients/details/details.page';
import { SearchComponent } from '../../search/search.component';
import { CompaniesContactsSelectPage as ContactsSelectPage } from 'src/app/modals/companies-contacts-select/companies-contacts-select.page';
import { CompaniesContactsSelectPage } from 'src/app/modals/companies-contacts-select/companies-contacts-select.page';
import { DefaultFolderStructurePage } from 'src/app/modals/default-folder-structure/default-folder-structure.page';
import { ManageComponent } from 'src/app/components/tags/manage/manage.component';
import { ManagerComponent } from '../../groups/manager/manager.component';
import { SelectComponent as TagsSelectPage } from 'src/app/components/tags/select/select.component';
import { ManagerComponent as GroupsManagerPage } from 'src/app/components/groups/manager/manager.component';
import { ImportPage } from 'src/app/clients/import/import.page';
import { DeletedClientsPage } from 'src/app/modals/deleted-clients/deleted-clients.page';
import { CookieService } from 'ngx-cookie-service';
import { ColumnPickerComponent, ColumnConfig } from '../../tables/column-picker/column-picker.component';



@Component({
  selector: 'app-clients-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, OnChanges {

  @Input() clients: any = null;
  non_grouped_clients: any[] = [];
  @Input() show_company_role = false;
  @Input() show_team_member_role = false;
  @Input() team_member_guid = '';
  team_member_roles: any[] = [];
  @Input() show_paging: any = null;
  @Input() hide_search = false;
  @Input() hide_create = false;
  @Input() url_mod = '';
  @Input() allow_hash_set = true;
  @Input() group_view = true;
  @Input() hide_empty_groups = false;
  @Input() hide_group_toggle = false;
  @Input() resetQuickSearchToken = 0;
  non_grouped_objects = [];
  non_grouped_expanded = false;
  loaded_clients = false;
  /**
   * Array of column keys to hide. Example: ['tags','groups'] to hide tags and groups columns
   */
  @Input() hideColumns: string[] = [];
  @Input() hide_column_picker = false;
  last_search: any;
  is_searching = false;
  query_title = '';

  /**
   * Column configuration for the column picker
   */
  availableColumns: ColumnConfig[] = [
    { key: 'active', label: 'Portal Access', visible: true },
    { key: 'display_name', label: 'Display Name', visible: true },
    { key: 'email', label: 'Email', visible: true },
    { key: 'team_member_role', label: 'Team Member Role', visible: true },
    { key: 'company_role', label: 'Company Role', visible: true },
    { key: 'custom_id', label: 'Custom ID', visible: true },
    { key: 'address', label: 'Address', visible: false },
    { key: 'groups', label: 'Groups', visible: true },
    { key: 'tags', label: 'Tags', visible: true },
    { key: 'team_members', label: 'Team Members', visible: true },
  ];

  // Enhanced search functionality
  all_clients: any[] = []; // Store original unfiltered clients
  current_search_query: any = null;
  quickSearchTerm = '';
  // What is typed in the box (shown immediately); quickSearchTerm is what the table is actually filtered by, which
  // follows it ~250ms after typing stops so the table isn't re-filtered on every keystroke
  quickSearchInput = '';
  private quickSearchTimer: any = null;
  // The lowercase text each row is searched in - built once per row instead of on every pass over the table
  private searchTextCache = new WeakMap<object, string>();
  show_all_contacts = true;

  @ViewChildren(Table) tables: QueryList<Table>;
  activeClientFilterLabels: string[] = [];
  // Not private - table.component.html binds this into app-filter-presets' [fieldLabelMap].
  readonly clientFilterLabelMap: { [field: string]: string } = {
    active: 'Portal Access',
    display_name: 'Name',
    email: 'Email',
    team_member_role: 'Role',
    groups: 'Groups',
    tags: 'Tags',
    team_members: 'Team Members',
  };

  onClientTableFilter(event: any) {
    this.activeClientFilterLabels = this.computeActiveFilterLabels(event.filters, this.clientFilterLabelMap);
  }

  clearClientFilters() {
    this.tables?.forEach(t => this.clearTableFilters(t));
    this.activeClientFilterLabels = [];
  }

  // Saved filter presets (see FilterPresetsComponent / components/projects/table's
  // GetCurrentTableFilters for the pattern this mirrors) - bundles the PrimeNG column filters with the
  // "above the table" toggle buttons (Show All/Just Clients, Show/Hide Empty Groups). Group View itself
  // is excluded on purpose - it's a display mode, not a filter.
  GetCurrentTableFilters = (): any => {
    return {
      columnFilters: (this.tables?.first as any)?.filters ?? null,
      toolbar: {
        show_all_contacts: this.show_all_contacts,
        hide_empty_groups: this.hide_empty_groups,
      }
    };
  };

  ApplyTableFilters(saved: any) {
    const isWrapped = !!saved && (('columnFilters' in saved) || ('toolbar' in saved));
    const columnFilters = isWrapped ? saved?.columnFilters : saved;
    const toolbar = isWrapped ? saved?.toolbar : null;

    const applied = columnFilters ? JSON.parse(JSON.stringify(columnFilters)) : {};
    this.tables?.forEach(t => {
      (t as any).filters = JSON.parse(JSON.stringify(applied));
      (t as any)._filter();
    });
    this.activeClientFilterLabels = this.computeActiveFilterLabels(applied, this.clientFilterLabelMap);

    if (toolbar) {
      if (typeof toolbar.show_all_contacts === 'boolean') this.show_all_contacts = toolbar.show_all_contacts;
      if (typeof toolbar.hide_empty_groups === 'boolean') this.hide_empty_groups = toolbar.hide_empty_groups;
      this.UpdateGroupsCount();
      this.UpdateFilterCookies();
    }
  }

  DescribeToolbarState = (toolbar: any): string[] => {
    const parts: string[] = [];
    parts.push(`Contacts: ${toolbar.show_all_contacts ? 'All Contacts' : 'Just Clients'}`);
    if (typeof toolbar.hide_empty_groups === 'boolean') {
      parts.push(`Empty Groups: ${toolbar.hide_empty_groups ? 'Hidden' : 'Shown'}`);
    }
    return parts;
  };

  private clearTableFilters(t: Table): void {
    if (!t) return;
    (t as any).tableService.onResetChange();
    if ((t as any).isStateful()) (t as any).clearState();
    (t as any)._filter();
  }

  private computeActiveFilterLabels(filters: any, labelMap: { [field: string]: string }): string[] {
    if (!filters) return [];
    const active: string[] = [];
    for (const field of Object.keys(labelMap)) {
      const raw = filters[field];
      if (!raw) continue;
      const constraints = Array.isArray(raw) ? raw : [raw];
      const hasValue = constraints.some((c: any) => {
        if (c.value === null || c.value === undefined) return false;
        if (Array.isArray(c.value)) return c.value.length > 0;
        if (typeof c.value === 'string') return c.value.trim().length > 0;
        return true;
      });
      if (hasValue) active.push(labelMap[field]);
    }
    return active;
  }

  first_rule = {
    field: 'Active',
    opr: 'equals',
    value: '',
    type: 'boolean'
  }

  fields = [
    {
      name: 'Active',
      type: 'boolean'
    },
    {
      name: 'Is Your Client',
      type: 'boolean'
    },
    {
      name: 'Display Name',
      type: 'string'
    },
    {
      name: 'Email',
      type: 'string'
    },
    {
      name: 'Groups',
      type: 'category',
      options: {

      }
    },
    {
      name: 'Team Members',
      type: 'category',
      options: {

      }
    },
    {
      name: 'Tags',
      type: 'category',
      options: {

      }
    }
  ];


  groups: any[];
  printersForFilter: any[] = [];
  tags: any[];
  team_members: any[];

  max_per_page = 100;
  @Output() clientModified = new EventEmitter<{ data: any }>();

  groupFilter: any[];
  matchModeOptions = [];

  inc_data = false;

  constructor(private modalController: ModalController, private apiService: ApiService, public globalService: GlobalService, private searchService: SearchService, private alertController: AlertController,private cookieService: CookieService, private router: Router) {

    this.matchModeOptions = [
      {
        label: 'Includes Any',
        value: "includesAny"
      },
      {
        label: 'Includes All',
        value: "includesAll"
      }
    ];

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['resetQuickSearchToken'] && !changes['resetQuickSearchToken'].firstChange) {
      this.onQuickSearchChange('');
    }
  }
  ngOnInit() {
    if(this.clients) this.inc_data = true;
    this.RestoreFilterCookies();
    this.initializeColumnVisibility();
    this.GetAllData();



  }

  /**
   * Initialize column visibility based on hideColumns input
   */
  initializeColumnVisibility() {
    // Columns hidden until the user turns them on in the column picker - the saved hideColumns (or the
    // parent's) predates them, so they'd otherwise just appear.
    const hidden = this.defaultHiddenColumns.filter(key =>
      !this.shownDefaultHiddenColumns.includes(key) && !this.hideColumns.includes(key));
    if (hidden.length) this.hideColumns = [...this.hideColumns, ...hidden];
    this.syncColumnsFromHideColumns();
  }

  // Columns that start hidden (see initializeColumnVisibility), and which of them the user has turned on.
  private readonly defaultHiddenColumns = ['address'];
  private shownDefaultHiddenColumns: string[] = [];

  ClientAddress(client: any): string {
    return FormatAddress(client?.str_address, client?.city, client?.province, client?.postal_code, client?.country);
  }

  ClientMapsUrl(client: any): string {
    return GoogleMapsUrl(this.ClientAddress(client));
  }

  // Puts the columns in the order the column picker left them (saved with the other table preferences). Columns the
  // saved order doesn't know about - added since - go after the known ones, in their default order.
  private ApplyColumnOrder(order: string[]) {
    const position = (key: string) => { const i = order.indexOf(key); return i < 0 ? order.length : i; };
    this.availableColumns = [...this.availableColumns].sort((a, b) => position(a.key) - position(b.key));
  }

  /**
   * Sync availableColumns visibility state from hideColumns array
   */
  syncColumnsFromHideColumns() {
    this.availableColumns.forEach(col => {
      col.visible = !this.hideColumns.includes(col.key);
    });
  }

  /**
   * Sync hideColumns array from availableColumns visibility state
   */
  syncHideColumnsFromColumns() {
    this.hideColumns = this.availableColumns
      .filter(col => !col.visible)
      .map(col => col.key);
    this.shownDefaultHiddenColumns = this.defaultHiddenColumns.filter(key => !this.hideColumns.includes(key));
  }

  // Admin-only (the Manage button itself is disabled for non-admins - see table.component.html) deep
  // link straight to the Contacts section of Manage Firm, rather than always landing on Team Members
  // (see ManageFirmPage.ngOnInit's ?section= handling).
  GoToManageFirm() {
    this.router.navigate(['/manage-firm'], { queryParams: { section: 'contacts' } });
  }

  /**
   * Open the column picker modal
   */
  async OpenColumnPicker() {
    const columnsCopy: ColumnConfig[] = this.availableColumns.map(col => ({ ...col }));

    const modal = await this.modalController.create({
      component: ColumnPickerComponent,
      cssClass: 'small-modal tall-modal',
      componentProps: {
        columns: columnsCopy
      },
      backdropDismiss: false
    });

    modal.onDidDismiss().then((data: any) => {
      if (data.data && data.data.columns) {
        this.availableColumns = data.data.columns;
        this.syncHideColumnsFromColumns();
        this.UpdateFilterCookies();
      }
    });

    return await modal.present();
  }

  async GetAllData(force_refresh = false) {
   this.loaded_clients = false;
    this.GetTags();
    this.GetTeamMembers();

    await this.GetGroups(force_refresh);
    // Only default to 25 the first time (no saved preference yet) - don't clobber a restored one
    if (this.show_paging && !this.restored_max_per_page) this.max_per_page = 25;
    if (!this.inc_data) await this.GetClients(force_refresh);
    this.UpdateGroupsCount();
    this.loaded_clients = true;
  }

  // keep_search_cache is only true when the search text changed - any other call means the data may have changed,
  // so the cached search text of every row is dropped
  UpdateGroupsCount(keep_search_cache = false) {
    if (!keep_search_cache) this.searchTextCache = new WeakMap<object, string>();
    this.non_grouped_objects = [];
    this.non_grouped_clients = [];
    if(!this.clients) return;
    for (const client of this.clients) {
      if (!client.groups || client.groups.length === 0) {
        if(!this.show_all_contacts && !client.is_a_client) continue;
        if (!this.MatchesQuickSearch(client)) continue;
        this.non_grouped_objects.push(client);
      }
    }
    if (!this.groups || !this.clients) {
      return;
    }
    for (const group of this.groups) {
      group.objects = [];
      for (const client of this.clients) {
        if(!this.show_all_contacts && !client.is_a_client) continue;
        if (!this.MatchesQuickSearch(client)) continue;
        if (client.groups && Array.isArray(client.groups)) {
          if (client.groups.some(g => g.guid === group.guid)) {
            group.objects.push(client);
          }
        }
      }
      group.clients = this.GetFilteredClients(this.clients,group,false);
    }
    this.non_grouped_clients = this.GetFilteredClients(this.clients,null,true);

    for (const group of this.groups) {
      if (group.parent_guid) {
        const parent_group = this.groups.find(g => g.guid === group.parent_guid);
        if (parent_group) {
          for (const obj of group.objects) {
            if (!parent_group.objects.some(o => o.guid === obj.guid)) {
              parent_group.objects.push(obj);
            }
          }
        }
      }
    }
  }



  async GetClients(force_refresh = false) {
    let data: any = null;
    if(this.url_mod == '') data = await this.apiService.GetClients(force_refresh);
    else data = await this.apiService.get('clients' + this.url_mod);
    if (data.success) {
      if(!data.data) data.data = [];
      // Store original clients and handle search state
      this.all_clients = [...data.data];

      // If we have an active search, reapply it to the new data
      if (this.is_searching && this.current_search_query) {
        const fieldMappingConfig = this.searchService.createClientFieldMapping();
        this.clients = this.searchService.filterDataByQuery(this.all_clients, this.current_search_query, fieldMappingConfig);
      } else {
        this.clients = [...this.all_clients];
        this.is_searching = false;
        this.query_title = '';
      }
      this.UpdateGroupsCount();
    }

    if(!this.clients) this.clients = [];
    this.CheckHash();
    if (this.show_team_member_role) {
      // Get the roles for the team members and find the matching role for each client
      this.GetTeamMemberRoles();
    }
  }

  async CheckHash() {
    if (!this.allow_hash_set) return;
    if (window.location.hash.replace('#', '')) {
      let hash_check = window.location.hash.replace('#', '');
      if (hash_check.length > 1) {
        let index = this.clients.findIndex(item => item.guid === hash_check);
        if (index >= 0) this.presentDetailsModal(this.clients[index]);
      }
    }
  }


  GetCommentTooltip(item: any): string {
    if (!item.client_last_comment_date) return 'No comments yet — click to add';
    return `Last comment: ${dayjs(this.globalService.DateToUTCFormat(item.client_last_comment_date)).format('MMM D, YYYY h:mm A')}`;
  }

  async OpenAddComment(item: any) {
    const modal = await this.modalController.create({
      component: QuickCommentComponent,
      cssClass: 'small-modal',
      componentProps: { url: 'client/comments', guid: item.guid, title: item.display_name },
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.success) item.client_comment_count = (item.client_comment_count ?? 0) + 1;
  }

  async OpenQuickComment(item: any) {
    const modal = await this.modalController.create({
      component: DetailsPage,
      cssClass: 'large-modal',
      componentProps: { client: item, activeIndex: 'comments' },
      backdropDismiss: false,
    });
    await modal.present();
  }

  async presentDetailsModal(client) {
    if (this.allow_hash_set) window.location.hash = client.guid;
    const modal = await this.modalController.create({
      component: DetailsPage,
      cssClass: 'large-modal',
      componentProps: {
        client: client
      },
      backdropDismiss: false

    })
    modal.onDidDismiss().then((data: any) => {
      window.location.hash = '';
      if (data.data) {
        if(data.data.deleted) this.GetAllData(true);
        else{
          let client = data.data;
        let index = this.clients.findIndex(item => item.guid === client.guid);
        this.clients[index] = client;
        this.UpdateGroupsCount();
        }
        
      }
    });
    return await modal.present()
  }

  async UpdateActive(client) {
    if (!this.globalService.Can('contacts_update')) return;
    const alert = await this.alertController.create({
      header: (client.active ? 'Deactivate' : 'Activate') + ' Client',
      message: 'Are you sure you want to ' + (client.active ? 'deactivate' : 'activate') + ' this client?',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: async () => {
            client.active = !client.active;
            let data: any = await this.apiService.put(`clients/${client.guid}/activate`, client);
            this.globalService.presentToastMessage(data.message, data.success);
            if (!data.success) {
              client.active = !client.active; // Revert the change if the update failed
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async GetGroups(force_refresh = false) {
    let data: any = await this.apiService.GetGroups('clients', force_refresh);
    this.groups = data.data;
    if(!this.groups) this.groups = [];
    this.UpdateGroupsCount();
    //this.groups = Object.values([...new Set(this.groups)]).map(x => ({ id: x.guid, value: x.title}));
  }
  async GetTeamMembers() {
    let data: any = await this.apiService.get('team_members');
    this.team_members = data.data;
    if(!this.team_members) this.team_members = [];
  }

  async GetTags() {
    let data: any = await this.apiService.get('tags');
    this.tags = data.data;
    if(!this.tags) this.tags = [];
  }

  async OpenCreateClientModal() {
    const modal = await this.modalController.create({
      component: CreatePage,
      cssClass: 'large-modal',
      backdropDismiss: false
    })
    modal.onDidDismiss().then((data: any) => {
      // Local push, no re-fetch - group.clients/non_grouped_clients (what the table actually
      // renders) are only rebuilt by UpdateGroupsCount(), so the new contact needs that called
      // explicitly rather than just landing in this.clients.
      if (data.data) {
        if (!this.clients) this.clients = [];
        this.clients.push(data.data);
        if (!this.all_clients) this.all_clients = [];
        this.all_clients.push(data.data);
        this.UpdateGroupsCount();
      }
    });
    return await modal.present()
  }

  async OpenSearchModal() {
    const modal = await this.modalController.create({
      component: SearchComponent,
      cssClass: 'small-modal tall-modal',
      componentProps: {
        type: 'clients',
        show_date_range: false,
        get_groups: true,
        get_tags: true,
        get_status: false,
        get_team_members: true,
        first_rule: this.first_rule,
        fields: this.fields
      }
    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.SearchClients(data.data.rules);
      }
    });
    return await modal.present()
  }



  async SearchClients(query) {
    this.query_title = query.title || '';
    this.last_search = query;
    this.is_searching = true;
    this.current_search_query = query;

    // Save current view and switch to table view for searching
    this.UpdateFilterCookies();
    this.group_view = false;

    // Use the search service to filter clients locally
    const fieldMappingConfig = this.searchService.createClientFieldMapping();
    const filteredClients = this.searchService.filterDataByQuery(this.all_clients, query, fieldMappingConfig);

    this.clients = filteredClients;
    this.UpdateGroupsCount();
  }

  async GetTeamMemberRoles() {
    let roles_data: any = await this.apiService.get('firms/team_members/roles');
    this.team_member_roles = roles_data.data;
    if(!this.clients) return;
    for (const client of this.clients) {
      if (client.team_members && Array.isArray(client.team_members)) {
        for (const team_member of client.team_members) {
          if (team_member.guid == this.team_member_guid) {
            const matchingRole = this.team_member_roles.find(role => role.guid === team_member.role_guid);
            client.team_member_role_title = matchingRole ? matchingRole.title : '';
            client.team_member_role_guid = matchingRole ? matchingRole.guid : '';
          }
        }
      }
    }
  }

  GetRoleColour(role_guid) {
    return this.team_member_roles.find(role => role.guid === role_guid)?.colour || '#000000';
  }

  // async onTeamMemberRoleChange(client: any, newRoleGuid: string) {
  //   let team_member = {
  //     guid: this.team_member_guid,
  //     role_id: '',
  //     role_guid: newRoleGuid
  //   }
  //   let data: any = await this.apiService.put(`clients/team_members/${client.guid}`, team_member);
  //   this.globalService.presentToastMessage(data.message, data.success);

  // }

  //Here we need to be able to select clients and add them to a team member
  async presentSelectClientsModal() {
    let clients_copy = JSON.stringify(this.clients);
    const modal = await this.modalController.create({
      component: CompaniesContactsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        selecting_for_team_member: true,
        hide_companies: true,
        selected_clients: this.clients,
        remove_clients: this.clients,
        team_member_roles: this.team_member_roles,
        in_clients: [],
        target_header: 'Currently Selected'
      }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data && data.data.clients) {
        clients_copy = JSON.parse(clients_copy);
        this.CheckClients(clients_copy, data.data.clients);
      }
      else {
        this.clients = [...JSON.parse(clients_copy)];
        if(!this.clients) this.clients = [];
      }

    });
    return await modal.present();
  }

  // See if we have to remove or add clients for the team member
  async CheckClients(old_clients, new_clients) {
    await this.globalService.presentLoading("Updating Clients...", 9000000);
    // Remove clients that are no longer selected
    // Remove clients that are no longer selected
    for (const client of old_clients) {
      const index = new_clients.findIndex(e => client.guid === e.guid);
      if (index == -1) {
        await this.RemoveClientFromTeamMember(client);
      }
    }
    // Add new clients that were not previously selected
    for (const client of new_clients) {
      const index = old_clients.findIndex(e => client.guid === e.guid);
      if (index == -1) {
        await this.AddClientToTeamMember(client);
      }
    }
    // Update clients if their data has changed
    for (const client of new_clients) {
      const index = old_clients.findIndex(e => client.guid === e.guid);
      if (index >= 0) {
        if (JSON.stringify(client) != JSON.stringify(old_clients[index])) {
          await this.UpdateClientForTeamMember(client);
        }
      }
    }
    this.globalService.dismissLoading();
    this.GetClients();
  }

  // API calls to manage clients for the team member
  async RemoveClientFromTeamMember(client) {
    let team_member = {
      guid: this.team_member_guid,
      role_guid: client.team_member_role_guid
    }
    let data: any = await this.apiService.delete(`clients/team_members/${client.guid}`, team_member);
  }
  async AddClientToTeamMember(client) {
    let team_member = {
      guid: this.team_member_guid,
      role_guid: client.team_member_role_guid
    }
    let data: any = await this.apiService.post(`clients/team_members/${client.guid}`, team_member);
  }
  async UpdateClientForTeamMember(client) {
    let team_member = {
      guid: this.team_member_guid,
      role_guid: client.team_member_role_guid
    }
    let data: any = await this.apiService.put(`clients/team_members/${client.guid}`, team_member);
  }

  /**
   * Clears the current search and shows all clients
   */
  ClearSearch() {
    this.is_searching = false;
    this.current_search_query = null;
    this.query_title = '';
    this.last_search = null;
    if (this.all_clients.length > 0) {
      this.clients = [...this.all_clients];
    }
    // Restore view from cookie
    this.RestoreFilterCookies();
    this.UpdateGroupsCount();
  }

  async OpenManageDefaultFolders() {
    const modal = await this.modalController.create({
      component: DefaultFolderStructurePage,
      cssClass: 'tall-modal medium-modal',
      componentProps: {
        for_clients: 1
      },
    })
    modal.onDidDismiss().then((data: any) => {

    });
    return await modal.present()
  }

  async OpenManageTags() {
    const modal = await this.modalController.create({
      component: ManageComponent,
      cssClass: 'tall-modal medium-modal',
      componentProps: {
        url_base: 'tags',
        url: 'clients',
        local_only: true,
        title: "Manage Tags",
        name: "Tags",
      },
    })
    modal.onDidDismiss().then((data: any) => {

    });
    return await modal.present()
  }

  async OpenManageGroups() {
    const modal = await this.modalController.create({
      component: ManagerComponent,
      cssClass: 'tall-modal medium-modal',
      componentProps: {
        url_base: 'groups',
        url: 'clients',
        local_only: true,
        title: "Manage Groups",
        name: "Groups",
      },
    })
    modal.onDidDismiss().then((data: any) => {
      this.GetGroups();
    });
    return await modal.present()
  }

  ClientInGroup(client, group) {

    if (!client.groups || client.groups.length == 0) return false;
    for (const g of client.groups) {

      if (g.guid === group.guid) {
        return true;
      }
    }
    return false;
  }

  async OpenDeletedModal() {
    const modal = await this.modalController.create({
      component: DeletedClientsPage,
      cssClass: 'medium-modal tall-modal',
      backdropDismiss: false,
    });
    modal.onDidDismiss().then((data: any) => {
      if (data.data && data.data.restored) this.GetAllData(true);
    });
    return await modal.present();
  }

  HideRowLogic(client, group, showNoGroup) {
    if (!this.group_view) {
      return (this.show_all_contacts || client.is_a_client);
    }
    if (showNoGroup) {
      return (!client.groups || client.groups.length == 0) && (this.show_all_contacts || client.is_a_client);
    }
    else if (!group) {
      return (this.show_all_contacts || client.is_a_client);
    }
    else {
      return this.ClientInGroup(client, group) && (this.show_all_contacts || client.is_a_client);
    }
  }

  onQuickSearchChange(term: string) {
    this.quickSearchInput = term ?? '';
    if (this.quickSearchTimer) clearTimeout(this.quickSearchTimer);
    const apply = () => {
      this.quickSearchTimer = null;
      this.quickSearchTerm = this.quickSearchInput;
      this.UpdateGroupsCount(true);
    };
    // Clearing applies straight away, typing waits for a pause
    if (!this.quickSearchInput.trim()) apply();
    else this.quickSearchTimer = setTimeout(apply, 250);
  }

  private MatchesQuickSearch(client: any): boolean {
    const query = this.quickSearchTerm?.trim().toLowerCase();
    if (!query) return true;
    return this.SearchText(client).includes(query);
  }

  // Everything a contact can be searched by, lowercased and joined (hidden columns included; business number is
  // searched too, though it has no column). Built once per contact and reused until the data changes
  // (UpdateGroupsCount clears it).
  private SearchText(client: any): string {
    let text = this.searchTextCache.get(client);
    if (text !== undefined) return text;
    text = [
      client?.display_name, client?.email, client?.team_member_role_title, client?.company_role_name, client?.custom_id,
      this.ClientAddress(client), client?.business_number,
      ...(client?.groups || []).map((g: any) => g.title),
      ...(client?.tags || []).map((t: any) => t.title),
      client?.all_team_members ? 'All Team Members' : '',
      ...(client?.team_members || []).map((m: any) => m.display_name),
    ].map(value => `${value ?? ''}`.toLowerCase()).join('\u0001');
    this.searchTextCache.set(client, text);
    return text;
  }

  // Names every column the quick search looks in
  get QuickSearchPlaceholder(): string {
    const columns = ['display name', 'email', ...(this.show_team_member_role ? ['team member role'] : []),
      ...(this.show_company_role ? ['company role'] : []), 'custom ID', 'address', 'groups', 'tags', 'team members'];
    return 'Quick search by ' + columns.join(', ') + '...';
  }

 GetFilteredClients(clients: any[] | null | undefined, group: any, showNoGroup: boolean): any[] {
  const list = clients ?? [];
  return list.filter(c =>
    this.HideRowLogic(c, group, showNoGroup) &&
    this.MatchesQuickSearch(c) &&
    !c.filter_out_group &&
    !c.filter_out_tag &&
    !c.filter_out_team_member
  );
}

  UpdateFilterCookies() {
    const filterPreferences = {
      group_view: this.group_view,
      show_all_contacts: this.show_all_contacts,
      hide_empty_groups: this.hide_empty_groups,
      hideColumns: this.hideColumns,
      columnOrder: this.availableColumns.map(col => col.key),
      shownDefaultHiddenColumns: this.shownDefaultHiddenColumns,
      max_per_page: this.max_per_page
    };
    this.cookieService.set('clientTableFilters', JSON.stringify(filterPreferences), 30, '/');
  }

  // Rows-per-page changed from the paginator's own dropdown - PrimeNG doesn't have a two-way [(rows)]
  // binding, so this is caught via (onPage) instead, same as any other filter change.
  private restored_max_per_page = false;
  OnTablePageChange(event: any) {
    if (event?.rows && event.rows !== this.max_per_page) {
      this.max_per_page = event.rows;
      this.UpdateFilterCookies();
    }
  }

  getTableDescription(): string {
    const parts: string[] = [];

    const contactLabel = this.show_all_contacts ? 'all contacts' : 'clients only';
    parts.push(`Showing ${contactLabel}`);

    if (this.is_searching) {
      parts.push('a search filter is active');
    }
    if (this.quickSearchTerm?.trim()) {
      parts.push(`Quick searching for: ${this.quickSearchTerm.trim()}`);
    }

    return parts.length === 1 ? parts[0] + '.' : parts[0] + ', ' + parts.slice(1).join(', ') + '.';
  }

  RestoreFilterCookies() {
    const cookieValue = this.cookieService.get('clientTableFilters');
    if (cookieValue) {
      try {
        const filterPreferences = JSON.parse(cookieValue);
        // Don't restore group_view when searching
        if (!this.is_searching && filterPreferences.group_view !== undefined) this.group_view = filterPreferences.group_view;
        if (filterPreferences.show_all_contacts !== undefined) this.show_all_contacts = filterPreferences.show_all_contacts;
        if (filterPreferences.hide_empty_groups !== undefined) this.hide_empty_groups = filterPreferences.hide_empty_groups;
        if (filterPreferences.hideColumns !== undefined) this.hideColumns = filterPreferences.hideColumns;
        if (Array.isArray(filterPreferences.shownDefaultHiddenColumns)) this.shownDefaultHiddenColumns = filterPreferences.shownDefaultHiddenColumns;
        if (Array.isArray(filterPreferences.columnOrder)) this.ApplyColumnOrder(filterPreferences.columnOrder);
        if (filterPreferences.max_per_page !== undefined) {
          this.max_per_page = filterPreferences.max_per_page;
          this.restored_max_per_page = true;
        }
      } catch (e) {
        console.error('Error parsing filter cookies:', e);
      }
    }
  }


  async OpenImportModal() {
    const modal = await this.modalController.create({
      component: ImportPage,
      cssClass: 'large-modal',
      backdropDismiss: false
    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.GetClients();
      }
    });
    return await modal.present()
  }

  async OpenTagsModal(client: any) {
    if (!client.tags) client.tags = [];
    const tags_copy = JSON.stringify(client.tags);

    const modal = await this.modalController.create({
      component: TagsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'clients',
        client: client,
        selected_tags: client.tags
      }
    });

    modal.onDidDismiss().then((data: any) => {
      if (data && data.data) {
        const old_tags = JSON.parse(tags_copy);
        this.CheckClientTags(client, old_tags, data.data);
      } else {
        client.tags = JSON.parse(tags_copy);
      }
    });

    return await modal.present();
  }

  async CheckClientTags(client: any, old_tags: any[], new_tags: any[]) {
    await this.globalService.presentLoading('Updating Tags...', 9000000);
    old_tags.forEach(tag => {
      const index = new_tags.findIndex(e => tag.guid === e.guid);
      if (index == -1) {
        this.apiService.delete(`clients/tags/${client.guid}`, tag);
      }
    });
    new_tags.forEach(tag => {
      const index = old_tags.findIndex(e => tag.guid === e.guid);
      if (index == -1) {
        this.apiService.post(`clients/tags/${client.guid}`, tag);
      }
    });
    client.tags = [...new_tags];
    this.clientModified.emit({ data: client });
    this.globalService.dismissLoading();
  }

  async OpenGroupsModal(client: any) {
    if (!client.groups) client.groups = [];
    const groups_copy = JSON.stringify(client.groups);

    const modal = await this.modalController.create({
      component: GroupsManagerPage,
      cssClass: 'medium-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'clients',
        selection_mode: true,
        max_selection: 999,
        selected_groups: [...client.groups],
        modal_title: 'Select Groups'
      }
    });

    modal.onDidDismiss().then((data: any) => {
      if (data && data.data) {
        const old_groups = JSON.parse(groups_copy);
        this.CheckClientGroups(client, old_groups, data.data);
      }
    });

    return await modal.present();
  }

  async CheckClientGroups(client: any, old_groups: any[], new_groups: any[]) {
    await this.globalService.presentLoading('Updating Groups...', 9000000);
    old_groups.forEach(group => {
      const index = new_groups.findIndex(e => group.guid === e.guid);
      if (index == -1) {
        this.apiService.delete(`groups/clients/${client.guid}`, group);
      }
    });
    new_groups.forEach(group => {
      const index = old_groups.findIndex(e => group.guid === e.guid);
      if (index == -1) {
        this.apiService.post(`groups/clients/${client.guid}`, group);
      }
    });
    client.groups = [...new_groups];
    this.clientModified.emit({ data: client });
    this.UpdateGroupsCount();
    this.globalService.dismissLoading();
  }

}
